/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { BrandColors, Tag } from '@blend/enterprise-components';
import { Field } from 'react-final-form';
import { RadioButton, Text } from 'grommet';

const styles = StyleSheet.create({
  crossMark: {
    color: BrandColors.ACCENT_RED_DARK,
    fontWeight: 'bold',
  },
  descriptionLineItem: {
    marginBottom: 4,
  },
  descriptionTextBlock: {
    marginLeft: 25,
  },
  descriptionTextDisabledBlock: {
    opacity: 0.5,
  },
  optionLabel: {
    marginBottom: 8,
  },
  radioCardContainer: {
    ':last-child': {
      marginBottom: 50,
    },
    border: `1px solid ${BrandColors.CLOUD_50}`,
    borderRadius: 3,
    marginBottom: 24,
    padding: 24,
  },
  tickMark: {
    color: BrandColors.ACCENT_GREEN,
  },
});

interface NotaryOptionsProp {
  checkedValue: string | null;
  handleOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: () => void;
  isBlendDisabled: boolean;
}

export const NotaryOptions: React.FC<NotaryOptionsProp> = ({
  handleSubmit,
  handleOnChange,
  checkedValue,
  isBlendDisabled,
}) => {
  const isRadioChecked = (value: string | null): boolean => {
    return value === checkedValue;
  };
  const NOTARY_CAM = 'NOTARY_CAM';
  const OTHER = 'OTHER';

  const notAvailableYetPill = <Tag text="Not Available Yet" theme="warning" />;
  const notaryOptionConfigs = [
    {
      checked: isRadioChecked(NOTARY_CAM),
      disabled: isBlendDisabled,
      descriptions: [
        { boldedText: 'Document tagging', restOfText: 'is handled by Blend' },
        { boldedText: 'Closing appointment scheduling', restOfText: 'is handled by Blend' },
        { boldedText: 'Notary assignment', restOfText: 'is handled by Blend' },
        {
          boldedText: 'Signed closing documents',
          restOfText: 'will be sent directly to lender, settlement agent, and borrower',
        },
      ],
      label: 'Use Blend as the RON platform',
      symbol: '✓',
      symbolStyles: styles.tickMark,
      value: NOTARY_CAM,
      pill: notAvailableYetPill,
    },
    {
      checked: isRadioChecked(OTHER),
      descriptions: [
        { boldedText: 'Document tagging', restOfText: 'must be done manually by settlement agent' },
        { boldedText: 'Closing appointment scheduling', restOfText: 'must be handled by settlement agent' },
        { boldedText: 'Notary assignment', restOfText: 'must be coordinated by settlement agent' },
        {
          boldedText: 'Signed closing documents',
          restOfText: 'must be manually uploaded post-closing by settlement agent',
        },
      ],
      label: 'Use a different RON platform',
      symbol: '⨉',
      symbolStyles: styles.crossMark,
      value: OTHER,
    },
  ];

  return (
    <form onSubmit={handleSubmit}>
      <div>
        {notaryOptionConfigs.map((data, index) => {
          return (
            <div className={css(styles.radioCardContainer)} key={`radioCardContainer-${index}`}>
              <div className={css(styles.optionLabel)}>
                <Field
                  checked={data.checked}
                  name="ronPlatform"
                  onChange={handleOnChange}
                  type="radio"
                  value={data.value}
                >
                  {props => (
                    <RadioButton
                      checked={props.input.checked}
                      disabled={data.disabled}
                      label={
                        <>
                          <Text color={BrandColors.NIGHT} size={'18px'}>
                            {data.label}
                          </Text>
                          {data.disabled ? data.pill : ''}
                        </>
                      }
                      name={props.input.name}
                      onChange={e => {
                        handleOnChange(e);
                        props.input.onChange(e);
                      }}
                      value={props.input.value}
                    />
                  )}
                </Field>
              </div>
              {data.descriptions.map((lineItem, lineIndex) => {
                return (
                  <div
                    className={css(styles.descriptionTextBlock, data.disabled && styles.descriptionTextDisabledBlock)}
                    key={`${index}-${lineIndex}-description`}
                  >
                    <Text className={css(data.symbolStyles)}>{data.symbol} </Text>
                    <Text color={BrandColors.NIGHT}>
                      <Text weight="bold">{lineItem.boldedText} </Text>
                      {lineItem.restOfText}
                    </Text>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </form>
  );
};
